import React, { Suspense, useCallback } from "react";

import {
  PlanName,
  RecurrenceInterval,
  useCurrentPlanStateLazyQuery,
} from "@/apollo/types";
import { Modal } from "@/components/elements/Modal";
import * as Sentry from "@sentry/react";
import { MakeGenerics, useNavigate, useSearch } from "@tanstack/react-location";

import { LocaleProvider } from "../../../shared/providers";

const CheckoutContainer = React.lazy(() => import("../CheckoutContainer"));
// Lazy for the same reason as CheckoutContainer: both pull in Stripe, and this
// module is reached eagerly from routes.tsx. A static import here would also
// leave the Suspense boundary below with nothing to suspend on.
const ConfirmSubscriptionContainer = React.lazy(() =>
  import("../ConfirmSubscriptionContainer").then((m) => ({
    default: m.ConfirmSubscriptionContainer,
  })),
);

type InitCheckoutParams = {
  billingPeriod: RecurrenceInterval.Year | RecurrenceInterval.Month;
  planName: PlanName;
  numConnectors?: number;
  numUsers?: number;
  numRows?: number;
};

type OpenCheckoutParams = {
  planName: PlanName;
  billingPeriod?: RecurrenceInterval;
  numConnectors?: number;
  numUsers?: number;
  numRows?: number;
};

type LocationGenerics = MakeGenerics<{
  Search: { checkout: string | InitCheckoutParams };
}>;

export function useCheckoutModal() {
  const navigate = useNavigate<LocationGenerics>();
  const search = useSearch<LocationGenerics>();

  const isConfirmSubscription = search.checkout === "confirm_subscription";

  const planName =
    search.checkout && !isConfirmSubscription
      ? Object.values(PlanName).find(
          (x) =>
            x.toLowerCase() ===
            (typeof search.checkout === "string"
              ? search.checkout
              : search.checkout?.planName
            )?.toLowerCase(),
        )
      : undefined;

  const createOpenProps = (params: OpenCheckoutParams) => {
    const recurrenceInterval = params.billingPeriod ?? RecurrenceInterval.Year;
    return {
      search: {
        checkout: {
          planName: params.planName,
          billingPeriod: [
            RecurrenceInterval.Month,
            RecurrenceInterval.Year,
          ].includes(recurrenceInterval)
            ? recurrenceInterval
            : RecurrenceInterval.Year,
          numConnectors: params.numConnectors,
          numUsers: params.numUsers,
          numRows: params.numRows,
        } as InitCheckoutParams,
      },
    };
  };

  const onOpen = useCallback(
    (params: OpenCheckoutParams) => {
      navigate(createOpenProps(params));
    },
    [navigate],
  );

  const onClose = useCallback(() => {
    navigate({
      search: {
        checkout: undefined,
      },
    });
  }, [navigate]);

  const [fetchCurrentPlanState] = useCurrentPlanStateLazyQuery();
  const modifyCurrentPlan = useCallback(
    async (params: Partial<OpenCheckoutParams> = {}) => {
      const activeSubscriptionResp = await fetchCurrentPlanState();
      const activeSubscription = activeSubscriptionResp.data?.currentPlanState;
      if (!activeSubscription) {
        return;
      }
      const numConnectors =
        params.numConnectors || activeSubscription.plan.limits.connectors;
      const numUsers = params.numUsers || activeSubscription.plan.limits.users;
      const numRows = params.numRows || activeSubscription.plan.limits.rows;

      onOpen({
        planName: params.planName || activeSubscription.plan.name,
        billingPeriod:
          params.billingPeriod ||
          activeSubscription.subscription?.billingInterval,
        numConnectors,
        numUsers,
        numRows,
      });
    },
    [fetchCurrentPlanState, onOpen],
  );

  return {
    modifyCurrentPlan: modifyCurrentPlan,
    openCheckoutModal: onOpen,
    closeCheckoutModal: onClose,
    getLinkProps: (params: OpenCheckoutParams) => createOpenProps(params),
    isOpen: !!planName || isConfirmSubscription,
    initProps: {
      ...(typeof search.checkout !== "string" ? search.checkout : {}),
      planName,
    },
    isConfirmSubscription,
  };
}

export function CheckoutModalRoot() {
  const { closeCheckoutModal, initProps, isConfirmSubscription } =
    useCheckoutModal();
  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <Modal
        isOpen={!!initProps.planName}
        size="2xl"
        className="sm:max-w-6xl"
        centerOnScreen={false}
        onClose={() => {}}
      >
        <Suspense fallback={<div />}>
          {initProps.planName && (
            <LocaleProvider>
              <CheckoutContainer
                planName={initProps.planName}
                billingPeriod={initProps.billingPeriod}
                connectorCount={initProps.numConnectors}
                userCount={initProps.numUsers}
                rowsLimit={initProps.numRows}
                onClose={() => {
                  closeCheckoutModal();
                }}
              />
            </LocaleProvider>
          )}
        </Suspense>
      </Modal>
      <Modal
        isOpen={isConfirmSubscription}
        size="md"
        centerOnScreen={false}
        onClose={() => {}}
      >
        <Suspense fallback={<div />}>
          <ConfirmSubscriptionContainer
            onClose={() => {
              closeCheckoutModal();
            }}
          />
        </Suspense>
      </Modal>
    </Sentry.ErrorBoundary>
  );
}
